import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import type { AccumulatorReplyMessage } from '@/store/forwardAccumulator/api/multiPass/requestForwardAccumulator.model.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import type { TargetAccumulatorRfsRequest } from '@/store/targetAccumulator/api/multiPass/requestTargetAccumulator.model.ts';
import { getTargetAccumulatorRfsId } from '@/store/targetAccumulator/selectors.ts';
import { targetAccumulatorSlice } from '@/store/targetAccumulator/slice.ts';
import type { FxTargetAccumulator, TargetAccuType } from '@/store/targetAccumulator/state.ts';
import type { TargetLegProductType } from '@/types/common.ts';
import { type IQuoteAbort, isQuoteTraderNotification, isQuoteWithPrice, type QuoteReply } from '@/utils/rfs/type.ts';

export function requestTargetAccumulatorRfs(builder: WebapiEndpointBuilder) {
  return builder.mutation<{ skipCreditCheck: boolean }, TargetAccumulatorRfsRequest>({
    query: (params) => {
      return {
        url: '/fxAccumulator/rfs/request',
        method: 'POST',
        body: params.body,
        headers: {
          'X-Correlation-Id': params.rfsId,
        },
      };
    },
    transformResponse(_baseQueryReturnValue, _meta, arg) {
      return {
        skipCreditCheck: arg.body.fxTargetAccumulatorProduct.skipCreditCheck,
      };
    },
    async onCacheEntryAdded(params, { dispatch, cacheDataLoaded, cacheEntryRemoved, getState }) {
      const { tileId, rfsId } = params;

      // wait for the initial query to resolve before proceeding
      await cacheDataLoaded;

      const quoteParams = { tileId, rfsId, dispatch, getState: getState as () => AppState };

      const handleReceiveQuoteReply = buildHandleReceiveQuoteReply(quoteParams);
      const handleReceiveQuoteAbort = buildHandleReceiveQuoteAbort(quoteParams);

      const signalRConnector = getSignalRConnector();
      signalRConnector.on('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteAbort', handleReceiveQuoteAbort);

      await cacheEntryRemoved;
      signalRConnector.off('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteAbort', handleReceiveQuoteAbort);
    },
  });
}

interface QuoteParams {
  tileId: string;
  rfsId: string;
  dispatch: AppDispatch;
  getState: () => AppState;
}

function buildHandleReceiveQuoteReply(quoteParams: QuoteParams) {
  return (reply: QuoteReply<AccumulatorReplyMessage>) => {
    const { dispatch, tileId, rfsId } = quoteParams;

    if (!isQuoteValid(reply, quoteParams)) {
      return;
    }

    if (isQuoteWithPrice(reply)) {
      if (reply.AccumulatorQuoteReply?.RfsId !== rfsId) {
        return;
      }

      dispatch(
        targetAccumulatorSlice.actions.targetAccumulatorRfsQuoteReceived({
          tileId,
          message: reply,
        }),
      );
    } else if (isQuoteTraderNotification(reply)) {
      dispatch(
        targetAccumulatorSlice.actions.targetAccumulatorTraderNotificationReceived({
          tileId,
          message: reply,
        }),
      );
    }
  };
}

function buildHandleReceiveQuoteAbort(quoteParams: QuoteParams) {
  return (quoteAbort: IQuoteAbort) => {
    const { dispatch, tileId } = quoteParams;

    if (isQuoteValid(quoteAbort, quoteParams)) {
      dispatch(targetAccumulatorSlice.actions.targetAccumulatorRfsQuoteAborted({ tileId, reply: quoteAbort }));
    }
  };
}

function isQuoteValid(message: QuoteReply<AccumulatorReplyMessage>, quoteParams: QuoteParams): boolean {
  const { rfsId, tileId, getState } = quoteParams;
  if (message.RequestId !== rfsId) {
    return false;
  }

  const state = getState();
  const targetAccumulator = state.targetAccumulator[tileId];
  const targetAccumulatorRfsId = isDefined(targetAccumulator)
    ? getTargetAccumulatorRfsId(targetAccumulator)
    : undefined;

  if (isNotDefined(targetAccumulatorRfsId)) {
    // rfs was just cancelled
    return false;
  }

  if (message.RequestId !== targetAccumulatorRfsId) {
    // Old request still lingering (maybe cancel was unsuccessful, or we received a quote while cancelling)
    return false;
  }
  return true;
}

export const createTargetAccumulatorRfsRequestParams = (
  tile: FxTargetAccumulator,
  tileId: string,
  rfsId: string,
  clientId: number,
  signalRConnectionId: string | undefined,
  forceTradable = false,
  skipCreditCheck = false,
): TargetAccumulatorRfsRequest | undefined => {
  const targetAccumulator = tile.properties;

  const {
    currencyPair,
    clientsWay,
    expiryDate,
    deliveryDate,
    premiumDate,
    firstFixingDate,
    lastFixingDate,
    hedgeType,
    hedgeAmount,
    hedgePaymentDate,
    spotHedge,
    accuType,
    ekiTrigger,
    targetProfitType,
    target,
    settlementMode,
    premiumPriceType,
    expiryDateTenor,
    marginCurrency,
    notionalCurrencyIndex,
    notional,
    premiumTypeCurrencyIndex,
    strike,
    calendar,
    fixingReference1,
    fixingReference2,
    fixingFrequency,
    cashSettlementCurrency,
    cutOffMarketPlace,
    strikeComparator,
    leverage,
  } = targetAccumulator;

  return {
    tileId,
    rfsId,
    body: {
      fxTargetAccumulatorProduct: {
        currencyPair,
        priceType: premiumPriceType,
        accumulatorType: 'TargetAccumulator',
        accuType: accuType ?? 'TargetAccu',
        knockIn: isDefined(ekiTrigger) ? String(ekiTrigger) : undefined,
        ...(hedgeType === 'Live'
          ? {}
          : {
              hedge: hedgeType,
              hedgePrice: spotHedge,
              hedgeAmount,
              hedgePaymentDate: hedgePaymentDate === null ? undefined : new Date(hedgePaymentDate),
            }),
        targetProfitType,
        target: target !== null ? Number(target) : undefined,
        settlementMode,
        premiumDate: isDefined(premiumDate) ? new Date(premiumDate) : undefined,
        premiumCurrency: premiumTypeCurrencyIndex === 1 ? 'ccy1' : 'ccy2',
        way: clientsWay,
        notional: notional !== null ? String(notional) : undefined,
        notionalCurrency: notionalCurrencyIndex,
        leverageRatio: isDefined(leverage) ? String(leverage) : undefined,
        strike: strike !== null ? Number(strike) : undefined,
        firstFixing: isDefined(firstFixingDate) ? new Date(firstFixingDate) : undefined,
        expiryTenor: expiryDateTenor ?? undefined,
        fixingFrequency: fixingFrequency,
        lastFixing: isDefined(lastFixingDate) ? new Date(lastFixingDate) : undefined,

        expiryDate: isDefined(expiryDate) ? new Date(expiryDate) : undefined,
        deliveryDate: isDefined(deliveryDate) ? new Date(deliveryDate) : undefined,
        fixingReference1: fixingReference1 ?? '',
        fixingReference2: fixingReference2 ?? '',
        cashSettlementCurrency,
        skipCreditCheck,
        calendar,
        cutOffMarketPlace: cutOffMarketPlace ?? '',
        crossCurrency: marginCurrency,
        strikeComparator,
        product: mapProduct(accuType),
      },
      replyToStream: signalRConnectionId,
      rfsId,
      forceTradable,

      requesterCounterpartyId: clientId,
    },
  };
};

function mapProduct(accuType?: TargetAccuType): TargetLegProductType {
  switch (accuType) {
    case 'TargetAccu':
      return 'TargetAccumulator';
    case 'EKI':
      return 'TargetEkiAccumulator';
    case 'PIVOT':
      return 'PivotTargetAccumulator';
    case 'PIVOTEKI':
      return 'PivotTargetEkiAccumulator';
    default:
      return 'ProductTypeUnknown';
  }
}

export function getCurrenciesFromCurrencyPair(currencyPair: string | undefined): {
  currency1: string | undefined;
  currency2: string | undefined;
} {
  if (isNotDefined(currencyPair) || isEmpty(currencyPair)) {
    return { currency1: undefined, currency2: undefined };
  }

  const currencies = currencyPair.split('/');

  const [currency1, currency2] = currencies;

  return { currency1, currency2 };
}
